import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  FolderOpen, 
  Users, 
  BookOpen, 
  TrendingUp,
  Plus,
  ArrowRight,
  FileText,
  Calendar,
  ClipboardList,
  Bell,
  Globe
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingReminders, setPendingReminders] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [statsRes, remindersRes] = await Promise.all([
          axios.get(`${API}/stats`, { headers: getAuthHeaders() }),
          axios.get(`${API}/supplier-quotes/pending-reminders`, { headers: getAuthHeaders() }).catch(() => ({ data: { count: 0 } }))
        ]);
        setStats(statsRes.data);
        setPendingReminders(remindersRes.data.count || 0);
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [getAuthHeaders]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-CH', { style: 'currency', currency: 'CHF' }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-CH', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Layout>
      <div className="space-y-8 fade-in">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">
              Bonjour, {user?.name?.split(' ')[0]} 👋
            </h1>
          </div>
          <Link to="/new-quote">
            <Button className="btn-primary gap-2" data-testid="new-quote-btn">
              <Plus className="h-5 w-5" />
              Nouveau devis
            </Button>
          </Link>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="stat-card slide-up" style={{ animationDelay: '0.1s' }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Projets</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {loading ? "-" : stats?.projects_count || 0}
                  </p>
                  <p className="text-xs text-green-600 mt-1">En cours</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-green-50 flex items-center justify-center">
                  <FolderOpen className="h-6 w-6 text-[#1E8B5A]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card slide-up" style={{ animationDelay: '0.2s' }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total chiffré</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {loading ? "-" : formatCurrency(stats?.total_quoted_amount || 0)}
                  </p>
                  <p className="text-xs text-green-600 mt-1">Tous projets</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-green-50 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-[#1E8B5A]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card slide-up" style={{ animationDelay: '0.3s' }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Fournisseurs</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {loading ? "-" : stats?.suppliers_count || 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Enregistrés</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card slide-up" style={{ animationDelay: '0.4s' }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Documents</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {loading ? "-" : stats?.knowledge_docs_count || 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Base de connaissances</p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-amber-50 flex items-center justify-center">
                  <BookOpen className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alert for pending reminders */}
        {pendingReminders > 0 && (
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Bell className="h-5 w-5 text-orange-600" />
                  <p className="font-medium text-orange-800">
                    {pendingReminders} demande{pendingReminders > 1 ? "s" : ""} fournisseur{pendingReminders > 1 ? "s" : ""} sans réponse depuis plus de 10 jours
                  </p>
                </div>
                <Link to="/supplier-tracking">
                  <Button size="sm" variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-100">
                    Voir les relances
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick actions + Recent projects */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick actions */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg">Actions rapides</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to="/new-quote" className="block">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-[#1E8B5A] text-white hover:bg-[#166A43] transition-colors cursor-pointer">
                  <FileText className="h-5 w-5" />
                  <div className="flex-1">
                    <p className="font-medium">Créer un devis</p>
                    <p className="text-sm text-white/70">Chiffrage Automatisé</p>
                  </div>
                  <ArrowRight className="h-5 w-5" />
                </div>
              </Link>
              
              <Link to="/supplier-tracking" className="block">
                <div className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer">
                  <ClipboardList className="h-5 w-5 text-gray-600" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Suivi fournisseurs</p>
                    <p className="text-sm text-gray-500">Comparer les offres</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gray-400" />
                </div>
              </Link>
              
              <Link to="/knowledge" className="block">
                <div className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer">
                  <BookOpen className="h-5 w-5 text-gray-600" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Ajouter des tarifs</p>
                    <p className="text-sm text-gray-500">Importer Excel/PDF</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gray-400" />
                </div>
              </Link>

              <Link to="/price-search" className="block">
                <div className="flex items-center gap-4 p-4 rounded-xl border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer" data-testid="price-search-link">
                  <Globe className="h-5 w-5 text-blue-600" />
                  <div className="flex-1">
                    <p className="font-medium text-blue-900">Recherche multi-pays</p>
                    <p className="text-sm text-blue-600">Comparer les prix</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-blue-400" />
                </div>
              </Link>

              <Link to="/suppliers" className="block">
                <div className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer">
                  <Users className="h-5 w-5 text-gray-600" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Gérer fournisseurs</p>
                    <p className="text-sm text-gray-500">Contacts et e-mails</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gray-400" />
                </div>
              </Link>
            </CardContent>
          </Card>

          {/* Recent projects */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Projets récents</CardTitle>
              <Link to="/projects">
                <Button variant="ghost" size="sm" className="text-[#1E8B5A]">
                  Voir tout
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse flex items-center gap-4 p-4 rounded-xl bg-gray-100">
                      <div className="h-10 w-10 rounded-lg bg-gray-200" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-1/3 bg-gray-200 rounded" />
                        <div className="h-3 w-1/4 bg-gray-200 rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : stats?.recent_projects?.length > 0 ? (
                <div className="space-y-3">
                  {stats.recent_projects.map((project, idx) => (
                    <Link 
                      key={project.id} 
                      to={`/projects/${project.id}`}
                      className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:border-[#1E8B5A]/30 hover:bg-green-50/30 transition-all cursor-pointer"
                      data-testid={`recent-project-${idx}`}
                    >
                      <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center">
                        <FileText className="h-5 w-5 text-[#1E8B5A]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{project.name}</p>
                        <p className="text-sm text-gray-500">{project.client_name || "Sans client"}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">
                          {formatCurrency(project.total_amount || 0)}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Calendar className="h-3 w-3" />
                          {formatDate(project.created_at)}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FolderOpen className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Aucun projet pour le moment</p>
                  <Link to="/new-quote">
                    <Button className="mt-4 btn-primary" size="sm">
                      Créer votre premier devis
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Assistance info */}
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#1E8B5A] to-[#2E8B57] flex items-center justify-center">
                  <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Chiffrage intelligent</p>
                  <p className="text-sm text-gray-500">Moteurs de recherche et interpolation de prix</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="px-4 py-2 rounded-lg bg-green-50 text-green-700 text-sm font-medium">
                  Base de données
                </div>
                <div className="px-4 py-2 rounded-lg bg-blue-50 text-blue-700 text-sm font-medium">
                  Recherche web
                </div>
                <div className="px-4 py-2 rounded-lg bg-purple-50 text-purple-700 text-sm font-medium">
                  Interpolation
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
